import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin: 4rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #0d9488;
  text-align: center;
  font-style: italic;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const KnowHow: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const services = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          <line x1="12" y1="11" x2="12" y2="17"></line>
          <line x1="9" y1="14" x2="15" y2="14"></line>
        </svg>
      ),
      title: language === 'it' ? 'FORMAZIONE PROFESSIONALE' : 'PROFESSIONAL TRAINING',
      description: language === 'it' 
        ? 'Il nostro programma di formazione è progettato per darti le competenze e le conoscenze necessarie per avere successo nel business degli affitti a breve termine. Offriamo corsi online, webinar e risorse educative per aiutarti a comprendere i fondamenti della gestione degli affitti a breve termine, dall\'acquisizione dei clienti alla gestione operativa.'
        : 'Our training program is designed to give you the skills and knowledge you need to succeed in the short-term rental business. We offer online courses, webinars and educational resources to help you understand the fundamentals of short-term rental management, from customer acquisition to operational management.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      title: language === 'it' ? 'ASSISTENZA DEDICATA' : 'DEDICATED ASSISTANCE',
      description: language === 'it' 
        ? 'Il nostro team di supporto è qui per te ad ogni passo del tuo percorso imprenditoriale. Forniamo un supporto personalizzato per rispondere alle tue domande, risolvere problemi e offrire consigli pratici per affrontare le sfide che potresti incontrare lungo il percorso.'
        : 'Our support team is here for you every step of the way on your entrepreneurial journey. We provide personalized support to address your questions, troubleshoot problems, and offer practical advice to address challenges you may encounter along the way.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
      title: language === 'it' ? 'CONTRATTI E LEGALITÀ' : 'CONTRACTS AND LEGALITY',
      description: language === 'it' 
        ? 'Ti guidiamo attraverso il labirinto di contratti e normative legali nel settore degli affitti a breve termine. Dagli accordi di locazione alle leggi fiscali, ti aiutiamo a navigare in questo complesso panorama legale per garantire che tu sia conforme e protetto.'
        : 'We guide you through the labyrinth of contracts and legal regulations in the short-term rental sector. From lease agreements to tax laws, we help you navigate this complex legal landscape to ensure you are compliant and protected.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      title: language === 'it' ? 'SOFTWARE INTUITIVO' : 'INTUITIVE SOFTWARE',
      description: language === 'it' 
        ? 'Il nostro software è progettato per semplificare la gestione delle tue proprietà in affitto a breve termine. Con funzionalità avanzate per la prenotazione, la gestione delle prenotazioni, la comunicazione con gli ospiti e la gestione dei ricavi, avrai tutto ciò di cui hai bisogno per ottimizzare le operazioni e massimizzare i profitti.'
        : 'Our software is designed to simplify the management of your short-term rental properties. With advanced features for booking, reservation management, guest communication and revenue management, you\'ll have everything you need to streamline operations and maximize profits.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      ),
      title: language === 'it' ? 'CHANNEL MANAGER INTEGRATO' : 'INTEGRATED CHANNEL MANAGER',
      description: language === 'it' 
        ? 'Il nostro channel manager ti permette di gestire tutte le tue prenotazioni da un\'unica piattaforma. Connettiti a una vasta rete di canali di prenotazione online e sincronizza automaticamente disponibilità, prezzi e prenotazioni per massimizzare la tua visibilità e redditività.'
        : 'Our channel manager allows you to manage all your bookings from a single platform. Connect to a vast network of online booking channels and automatically synchronize availability, prices and reservations to maximize your visibility and profitability.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      ),
      title: language === 'it' ? 'PRODOTTI FISICI E DIGITALI' : 'PHYSICAL AND DIGITAL PRODUCTS',
      description: language === 'it' 
        ? 'Oltre agli asset digitali, offriamo anche una gamma di prodotti fisici e digitali per aiutarti a migliorare le tue operazioni. Dalle guide stampate alle risorse digitali scaricabili, abbiamo tutto ciò di cui hai bisogno per ottimizzare la tua strategia di gestione degli affitti a breve termine.'
        : 'In addition to digital assets, we also offer a range of physical and digital products to help you improve your operations. From printed guides to downloadable digital resources, we have everything you need to optimize your short-term rental management strategy.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Know How | J-O-H' : 'Know How | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'J-O-H fornisce tutta l\'attrezzatura e la formazione necessarie per raggiungere gli obiettivi nel settore degli affitti brevi' 
            : 'J-O-H provides all the equipment and training necessary to achieve the objectives in the short-term rental sector'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/affiliation">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>KNOW HOW</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'J-O-H fornisce tutta l\'attrezzatura e la formazione necessarie per raggiungere gli obiettivi.'
              : 'J-O-H provides all the equipment and training necessary to achieve the objectives.'}
          </Description>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <CardIcon>
                  {service.icon}
                </CardIcon>
                <CardTitle>{service.title}</CardTitle>
                <CardDescription>{service.description}</CardDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          <Description>
            {language === 'it' 
              ? 'In sintesi, con il nostro know-how, ti offriamo una soluzione completa per lanciare e far crescere la tua attività di affitti a breve termine. Dalla formazione al supporto, dalla gestione dei contratti al software intuitivo e al channel manager integrato, ti diamo gli strumenti e le risorse di cui hai bisogno per avere successo. Unisciti a noi e preparati a raggiungere i tuoi obiettivi nel mondo degli affitti a breve termine!'
              : 'In summary, with our know-how, we offer you a complete solution to launch and grow your short-term rental business. From training to support, contract management to intuitive software and integrated channel manager, we give you the tools and resources you need to succeed. Join us and get ready to achieve your goals in the world of short-term rentals!'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci' : 'Contact us'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default KnowHow; 